import React from "react";
import { useSearchParams } from "react-router-dom";

import {
  useActivateNewsMutation,
  useNewsDetailQuery,
  useNewsDownloadProgressQuery,
  useNewsListQuery,
  useNewsPreviewQuery,
  useNewsPublicationStatusQuery,
  useRollbackNewsMutation,
  useStageNewsMutation,
} from "../queries";
import type { NewsBundleMetadata, NewsListFilters, NewsListItem } from "../api";
import { ConfirmationDialog } from "../components/confirmation-dialog";
import { SafeMarkdown } from "../components/safe-markdown";
import { PrivateNewsWorkspaceShell } from "../components/workspace-shell";
import "@views/investments/index.scss";
import "../styles.scss";

const PAGE_SIZE = 25;

type DraftFilters = { from: string; to: string; ticker: string; source: string };
type ConfirmationAction = "activate" | "rollback" | null;

export default function PrivateNewsScreen(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const filters = filtersFromParams(params);
  const selectedID = params.get("news") || "";
  const [draft, setDraft] = React.useState<DraftFilters>({
    from: filters.from || "", to: filters.to || "", ticker: filters.ticker || "", source: filters.source || "",
  });
  const statusQuery = useNewsPublicationStatusQuery();
  const listQuery = useNewsListQuery(filters);
  const detailQuery = useNewsDetailQuery(selectedID);
  const activeBundle = statusQuery.data?.active || listQuery.data?.bundle;

  React.useEffect(() => {
    setDraft({ from: filters.from || "", to: filters.to || "", ticker: filters.ticker || "", source: filters.source || "" });
  }, [filters.from, filters.to, filters.ticker, filters.source]);

  function updateParams(changes: Record<string, string | null>): void {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  }

  function applyFilters(event: React.FormEvent): void {
    event.preventDefault();
    updateParams({
      from: draft.from || null,
      to: draft.to || null,
      ticker: draft.ticker.trim().toUpperCase() || null,
      source: draft.source.trim() || null,
      page: null,
      news: null,
    });
  }

  return (
    <PrivateNewsWorkspaceShell freshness={activeBundle?.freshness}>
      <section className="private-news-notice" aria-label="Source material notice">
        <strong>Unverified source material</strong>
        <span>Newsletter claims are research inputs, not measured facts, house forecasts, or trading instructions.</span>
      </section>

      <section className="private-news-publication-summary" aria-label="Private news bundle status">
        <div><strong>Active local bundle</strong><span>{activeBundle ? `${shortHash(activeBundle.version)} · ${activeBundle.itemCount.toLocaleString()} items` : "No private news bundle is active."}</span></div>
        <a href="#/swing-trading/operator">Manage publications in Operator</a>
      </section>

      <form className="private-news-filters" onSubmit={applyFilters}>
        <label><span>From</span><input type="date" value={draft.from} onChange={(event) => setDraft((value) => ({ ...value, from: event.target.value }))} /></label>
        <label><span>To</span><input type="date" value={draft.to} onChange={(event) => setDraft((value) => ({ ...value, to: event.target.value }))} /></label>
        <label>
          <span>Ticker</span>
          <select value={draft.ticker} onChange={(event) => setDraft((value) => ({ ...value, ticker: event.target.value }))}>
            <option value="">All tickers</option>
            {(listQuery.data?.availableTickers || []).map((ticker) => <option value={ticker} key={ticker}>{ticker}</option>)}
          </select>
        </label>
        <label>
          <span>Source</span>
          <select value={draft.source} onChange={(event) => setDraft((value) => ({ ...value, source: event.target.value }))}>
            <option value="">All sources</option>
            {(listQuery.data?.availableSources || []).map((source) => <option value={source} key={source}>{source}</option>)}
          </select>
        </label>
        <div className="private-news-filter-actions">
          <button type="submit">Apply</button>
          <button type="button" className="secondary" onClick={() => { setDraft({ from: "", to: "", ticker: "", source: "" }); updateParams({ from: null, to: null, ticker: null, source: null, page: null, news: null }); }}>Reset</button>
        </div>
      </form>

      <div className="private-news-workspace">
        <NewsList
          response={listQuery.data}
          loading={listQuery.isLoading}
          error={listQuery.error}
          selectedID={selectedID}
          onSelect={(id) => updateParams({ news: id })}
          onPage={(page) => updateParams({ page: page > 1 ? String(page) : null, news: null })}
        />
        {selectedID && (
          <NewsReader
            loading={detailQuery.isLoading}
            error={detailQuery.error}
            detail={detailQuery.data}
            onClose={() => updateParams({ news: null })}
          />
        )}
      </div>

    </PrivateNewsWorkspaceShell>
  );
}

export function PublicationOperations(): JSX.Element {
  const [actionMessage, setActionMessage] = React.useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [confirmationAction, setConfirmationAction] = React.useState<ConfirmationAction>(null);
  const statusQuery = useNewsPublicationStatusQuery();
  const stageMutation = useStageNewsMutation();
  const downloadProgressQuery = useNewsDownloadProgressQuery(stageMutation.isPending);
  const activateMutation = useActivateNewsMutation();
  const rollbackMutation = useRollbackNewsMutation();
  const stagedVersion = statusQuery.data?.staged[0]?.version || stageMutation.data?.bundle.version || "";
  const previewQuery = useNewsPreviewQuery(stagedVersion);
  const activeBundle = statusQuery.data?.active;
  const actionPending = stageMutation.isPending || activateMutation.isPending || rollbackMutation.isPending;
  const remoteVersion = statusQuery.data?.latestRemote?.bundleVersion || "";

  async function stageLatest(): Promise<void> {
    setActionMessage(null);
    try {
      const result = await stageMutation.mutateAsync();
      setActionMessage({ tone: "success", text: [result.alreadyStaged ? "Latest publication was already downloaded for review." : "Latest publication downloaded and validated for review.", ...result.warnings].join(" ") });
    } catch (error) { setActionMessage({ tone: "error", text: formatError(error) }); }
  }
  async function activateStaged(): Promise<void> {
    if (!stagedVersion) return;
    setActionMessage(null);
    try {
      const result = await activateMutation.mutateAsync(stagedVersion);
      stageMutation.reset();
      setConfirmationAction(null);
      setActionMessage({ tone: "success", text: [`Activated bundle ${shortHash(result.active.version)}.`, ...result.warnings].join(" ") });
    } catch (error) { setConfirmationAction(null); setActionMessage({ tone: "error", text: formatError(error) }); }
  }
  async function rollback(): Promise<void> {
    setActionMessage(null);
    try {
      const result = await rollbackMutation.mutateAsync();
      setConfirmationAction(null);
      setActionMessage({ tone: "success", text: [`Rolled back to bundle ${shortHash(result.active.version)}.`, ...result.warnings].join(" ") });
    } catch (error) { setConfirmationAction(null); setActionMessage({ tone: "error", text: formatError(error) }); }
  }
  return <>
    <PublicationPanel active={activeBundle} statusLoading={statusQuery.isLoading} statusError={statusQuery.error} status={statusQuery.data} stagedBundle={statusQuery.data?.staged[0] || stageMutation.data?.bundle} preview={previewQuery.data} previewLoading={previewQuery.isLoading} previewError={previewQuery.error} actionPending={actionPending} latestIsActive={Boolean(remoteVersion && activeBundle?.version === remoteVersion)} latestIsStaged={Boolean(remoteVersion && stagedVersion === remoteVersion)} downloadProgress={downloadProgressQuery.data} actionMessage={actionMessage} onStage={() => void stageLatest()} onActivate={() => setConfirmationAction("activate")} onRollback={() => setConfirmationAction("rollback")} />
    <ConfirmationDialog open={confirmationAction !== null} title={confirmationAction === "rollback" ? "Roll back private news?" : "Activate this publication?"} description={confirmationAction === "rollback" ? `The site will return to the previous validated bundle${statusQuery.data?.previousVersion ? ` ${shortHash(statusQuery.data.previousVersion)}` : ""}.` : `The reviewed bundle ${shortHash(stagedVersion)} will become the source shown throughout Private News.`} confirmLabel={confirmationAction === "rollback" ? "Roll back bundle" : "Activate publication"} pendingLabel={confirmationAction === "rollback" ? "Rolling back…" : "Activating…"} pending={activateMutation.isPending || rollbackMutation.isPending} tone={confirmationAction === "rollback" ? "warning" : "primary"} onCancel={() => setConfirmationAction(null)} onConfirm={() => confirmationAction === "rollback" ? void rollback() : void activateStaged()} />
  </>;
}

function PublicationPanel(props: {
  active?: NewsBundleMetadata | null;
  statusLoading: boolean;
  statusError: Error | null;
  status?: ReturnType<typeof useNewsPublicationStatusQuery>["data"];
  stagedBundle?: NewsBundleMetadata;
  preview?: ReturnType<typeof useNewsPreviewQuery>["data"];
  previewLoading: boolean;
  previewError: Error | null;
  actionPending: boolean;
  latestIsActive: boolean;
  latestIsStaged: boolean;
  downloadProgress?: ReturnType<typeof useNewsDownloadProgressQuery>["data"];
  actionMessage: { tone: "success" | "error"; text: string } | null;
  onStage: () => void;
  onActivate: () => void;
  onRollback: () => void;
}): JSX.Element {
  const staged = props.stagedBundle;
  const remote = props.status?.latestRemote;
  return (
    <details className="private-news-publication" open={!props.active}>
      <summary>
        <span><strong>Publication updates</strong><small>Download, review, activate, or roll back validated news bundles.</small></span>
        <span className={`private-news-state private-news-state--${props.status?.state || "unavailable"}`}>{formatState(props.status?.state)}</span>
      </summary>
      <div className="private-news-publication-body">
        {props.statusLoading && <div className="private-news-inline-state">Checking the remote publication marker…</div>}
        {props.statusError && <div className="private-news-message private-news-message--error">Remote publication status is unavailable. Active local news remains readable.</div>}
        {props.status?.warnings.map((warning) => <div className="private-news-message private-news-message--warning" key={warning}>{warning}</div>)}
        {!props.active && (
          <section className="private-news-first-use" aria-label="How to activate private news">
            <strong>How to start</strong>
            <ol>
              <li>Publish a sanitized news bundle for this environment.</li>
              <li>Download the latest publication and review its local diff.</li>
              <li>Activate the reviewed bundle. Reading stays local afterward.</li>
            </ol>
          </section>
        )}
        <div className="private-news-version-grid">
          <VersionCard label="Active local" bundle={props.active || null} />
          <article className="private-news-version-card">
            <span>Latest remote</span>
            <strong title={remote?.bundleVersion}>{remote ? shortHash(remote.bundleVersion) : "Unavailable"}</strong>
            <small>{remote ? `Published ${formatDateTime(remote.updatedAt)}` : "No validated remote marker available."}</small>
            {remote?.objectCount !== undefined && (
              <small>{remote.itemCount?.toLocaleString() || 0} items · {remote.objectCount.toLocaleString()} objects · {formatBytes(remote.totalBytes || 0)}</small>
            )}
          </article>
          <VersionCard label="Downloaded for review" bundle={staged || null} />
        </div>
        {props.downloadProgress && props.downloadProgress.state !== "idle" && (
          <DownloadProgress progress={props.downloadProgress} />
        )}
        {(props.preview || props.previewLoading) && (
          <div className="private-news-preview" aria-live="polite">
            <strong>Downloaded change preview</strong>
            {props.previewLoading ? <span>Calculating local diff…</span> : props.preview && (
              <>
                <div>
                  <span>{props.preview.added} added</span>
                  <span>{props.preview.changed} changed</span>
                  <span>{props.preview.removed} removed</span>
                  <span>{props.preview.tickerTagChanges} ticker-tag changes</span>
                  <span>{props.preview.sourceCountChanges.length} source-count changes</span>
                  <span>{props.preview.dateCountChanges.length} received-date changes</span>
                </div>
                <small>
                  {props.preview.removedUnsafeLinkCount === null
                    ? "Unsafe-link removal counts are unavailable in the V1 publication metadata."
                    : `${props.preview.removedUnsafeLinkCount} unsafe links removed by publication.`}
                </small>
              </>
            )}
          </div>
        )}
        {props.previewError && <div className="private-news-message private-news-message--error">The downloaded bundle diff is unavailable. Activation remains disabled until its status can be reviewed.</div>}
        {props.actionMessage && <div role="status" className={`private-news-message private-news-message--${props.actionMessage.tone}`}>{props.actionMessage.text}</div>}
        <div className="private-news-publication-actions">
          <button className="swing-button" type="button" onClick={props.onStage} disabled={props.actionPending || props.latestIsActive || props.latestIsStaged}>
            {props.actionPending
              ? "Working…"
              : props.latestIsActive
                ? "Latest publication is active"
                : props.latestIsStaged
                  ? "Latest publication is downloaded"
                  : "Download latest publication"}
          </button>
          <button className="swing-button" type="button" onClick={props.onActivate} disabled={props.actionPending || !staged || props.previewLoading || Boolean(props.previewError) || !props.preview}>Activate reviewed publication</button>
          <button type="button" className="swing-button secondary" onClick={props.onRollback} disabled={props.actionPending || !props.status?.previousVersion}>Roll back</button>
        </div>
        <p className="private-news-operation-note">Downloaded and active bundles live in persistent service data and survive container restarts. Only checking and downloading contact GCS.</p>
      </div>
    </details>
  );
}

function DownloadProgress({ progress }: { progress: NonNullable<ReturnType<typeof useNewsDownloadProgressQuery>["data"]> }): JSX.Element {
  const total = Math.max(progress.objectCount, 1);
  const completed = Math.min(progress.completedObjects, total);
  const percent = progress.objectCount > 0 ? Math.round((completed / progress.objectCount) * 100) : 0;
  const stateLabel = progress.state === "checking"
    ? "Checking publication"
    : progress.state === "downloading"
      ? "Downloading"
      : progress.state === "validating"
        ? "Validating bundle"
        : progress.state === "completed"
          ? "Download complete"
          : "Download failed";
  const startedAt = progress.startedAt ? new Date(progress.startedAt).getTime() : Number.NaN;
  const updatedAt = progress.updatedAt ? new Date(progress.updatedAt).getTime() : Number.NaN;
  const elapsedSeconds = Number.isFinite(startedAt) && Number.isFinite(updatedAt)
    ? Math.max(0, Math.round((updatedAt - startedAt) / 1000))
    : null;
  const remainingSeconds = progress.state === "downloading" && elapsedSeconds !== null && progress.completedObjects > 0
    ? Math.max(0, Math.round((elapsedSeconds / progress.completedObjects) * (progress.objectCount - progress.completedObjects)))
    : null;

  return (
    <section className={`private-news-download private-news-download--${progress.state}`} aria-live="polite" aria-label="Publication download progress">
      <header>
        <div><strong>{stateLabel}</strong><span>{progress.message}</span></div>
        <b>{percent}%</b>
      </header>
      <progress max={total} value={completed}>{percent}%</progress>
      <div className="private-news-download-metrics">
        <span><strong>{progress.completedObjects.toLocaleString()}</strong> / {progress.objectCount.toLocaleString()} objects</span>
        <span><strong>{formatBytes(progress.downloadedBytes)}</strong> downloaded from GCS</span>
        {(progress.reusedObjects || 0) > 0 && <span><strong>{progress.reusedObjects?.toLocaleString()}</strong> objects · {formatBytes(progress.reusedBytes || 0)} reused locally</span>}
        <span>{formatBytes(progress.totalBytes)} complete bundle</span>
        <span>{progress.itemCount.toLocaleString()} news items</span>
        {elapsedSeconds !== null && <span>{formatDuration(elapsedSeconds)} elapsed</span>}
        {remainingSeconds !== null && <span>About {formatDuration(remainingSeconds)} remaining</span>}
      </div>
    </section>
  );
}

function VersionCard({ label, bundle }: { label: string; bundle: NewsBundleMetadata | null }): JSX.Element {
  return (
    <article className="private-news-version-card">
      <span>{label}</span>
      <strong title={bundle?.version}>{bundle ? shortHash(bundle.version) : "None"}</strong>
      <small>{bundle ? `${bundle.itemCount.toLocaleString()} items · ${formatDateTime(bundle.generatedAt)}` : "No bundle available."}</small>
      {bundle && <small>Contract {bundle.contractVersion} · compatible {bundle.compatibility.minimum}–{bundle.compatibility.maximum}</small>}
    </article>
  );
}

function NewsList(props: {
  response?: ReturnType<typeof useNewsListQuery>["data"];
  loading: boolean;
  error: Error | null;
  selectedID: string;
  onSelect: (id: string) => void;
  onPage: (page: number) => void;
}): JSX.Element {
  if (props.loading && !props.response) return <section className="private-news-list private-news-inline-state">Reading the active local index…</section>;
  if (props.error && !props.response) return <section className="private-news-list private-news-empty"><strong>No private news is active.</strong><span>Open Publication updates, download the latest publication, review the changes, and activate it.</span></section>;
  const response = props.response;
  if (!response || response.items.length === 0) return <section className="private-news-list private-news-empty"><strong>No matching items</strong><span>Adjust the date, ticker, or source filters.</span></section>;
  return (
    <section className="private-news-list" aria-label="Private news items">
      <header><div><span className="private-news-eyebrow">Active bundle</span><h2>Source inbox</h2></div><span>{response.total.toLocaleString()} results</span></header>
      <div className="private-news-list-items">
        {response.items.map((item) => <NewsListRow key={item.id} item={item} selected={props.selectedID === item.id} onSelect={props.onSelect} />)}
      </div>
      <footer>
        <button type="button" className="secondary" disabled={response.page <= 1} onClick={() => props.onPage(response.page - 1)}>Previous</button>
        <span>Page {response.page}</span>
        <button type="button" className="secondary" disabled={!response.hasMore} onClick={() => props.onPage(response.page + 1)}>Next</button>
      </footer>
    </section>
  );
}

function NewsListRow({ item, selected, onSelect }: { item: NewsListItem; selected: boolean; onSelect: (id: string) => void }): JSX.Element {
  return (
    <button type="button" className={`private-news-list-row${selected ? " selected" : ""}`} onClick={() => onSelect(item.id)}>
      <span className="private-news-list-row-top"><strong>{item.sourceName}</strong><time dateTime={item.receivedAt}>{formatDateTime(item.receivedAt)}</time></span>
      <span className="private-news-list-title">{item.title}</span>
      <span className="private-news-list-excerpt">{item.excerpt}</span>
      {item.tickers.length > 0 && <span className="private-news-tags">{item.tickers.map((tag) => <em key={`${tag.symbol}-${tag.relevance}`}>{tag.symbol}<small>{tag.relevance}</small></em>)}</span>}
    </button>
  );
}

function NewsReader(props: {
  detail?: ReturnType<typeof useNewsDetailQuery>["data"];
  loading: boolean;
  error: Error | null;
  onClose: () => void;
}): JSX.Element {
  const dialogRef = React.useRef<HTMLElement>(null);
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const returnFocusRef = React.useRef<HTMLElement | null>(null);
  const onCloseRef = React.useRef(props.onClose);
  onCloseRef.current = props.onClose;

  React.useEffect(() => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => closeRef.current?.focus());

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      ));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => returnFocusRef.current?.focus());
    };
  }, []);

  return (
    <div
      className="watchlist-modal-backdrop private-news-reader-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) props.onClose();
      }}
    >
      <aside
        ref={dialogRef}
        className="investments-card private-news-reader"
        role="dialog"
        aria-modal="true"
        aria-label={props.detail?.record.title || "Private news reader"}
      >
        <button ref={closeRef} type="button" className="private-news-reader-close" onClick={props.onClose}>Close reader</button>
        {props.loading && <div className="private-news-inline-state">Reading validated local content…</div>}
        {props.error && <div className="private-news-message private-news-message--error">This item is unavailable from the active bundle.</div>}
        {props.detail && (
          <article>
            <div className="private-news-reader-source">{props.detail.record.source.name} · {humanize(props.detail.record.source.classification)}</div>
            <h2>{props.detail.record.title}</h2>
            <div className="private-news-reader-dates">
              <span>Received {formatDateTime(props.detail.record.receivedAt)}</span>
              <span>Published {props.detail.record.publishedAt ? formatDateTime(props.detail.record.publishedAt) : "not supplied"}</span>
            </div>
            {props.detail.tickers.length > 0 && <div className="private-news-tags">{props.detail.tickers.map((tag) => <em key={`${tag.symbol}-${tag.relevance}`}>{tag.symbol}<small>{tag.relevance}</small></em>)}</div>}
            <div className="private-news-reader-warning">The text below is sanitized source material and has not been independently verified.</div>
            <SafeMarkdown content={props.detail.content} />
            {props.detail.record.links.length > 0 && (
              <section className="private-news-source-links">
                <h3>Sanitized source links</h3>
                {props.detail.record.links.map((link) => <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer">{link.host} · {humanize(link.type)}</a>)}
              </section>
            )}
            <footer>Content hash <code title={props.detail.record.content.sha256}>{shortHash(props.detail.record.content.sha256)}</code></footer>
          </article>
        )}
      </aside>
    </div>
  );
}

function filtersFromParams(params: URLSearchParams): NewsListFilters {
  const pageValue = Number(params.get("page") || "1");
  return {
    from: params.get("from") || undefined,
    to: params.get("to") || undefined,
    ticker: params.get("ticker") || undefined,
    source: params.get("source") || undefined,
    page: Number.isInteger(pageValue) && pageValue > 0 ? pageValue : 1,
    pageSize: PAGE_SIZE,
  };
}

function shortHash(value: string): string {
  return value ? `${value.slice(0, 10)}…${value.slice(-6)}` : "N/A";
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown time";
  return date.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const amount = value / (1024 ** index);
  return `${amount.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatDuration(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
}

function formatState(value?: string): string {
  if (!value) return "Unavailable";
  return value.split("-").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function humanize(value: string): string {
  return value.replace(/[-_]/g, " ");
}

function formatError(error: unknown): string {
  return error instanceof Error ? error.message : "Private news operation failed.";
}
